// src/App.tsx
import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { notifyEmbedNavigation } from "./utils/embedResize";
import { trackHubSpotStepView } from "./utils/hubspotTracking";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import Step4 from "./components/Step4";
import Step5 from "./components/Step5";
import Step6 from "./components/Step6";
import { Golfer, UpdateFieldFn } from "./types/types";
import Confirm from "./components/Confirm";
import Login from "./components/offers/Login";
import AdminSetupWizard from "./components/admin/AdminSetupWizard";
import OffersVerify from "./components/offers/OffersVerify";
import OffersList from "./components/offers/OffersList";
import GroupList from "./components/offers/GroupList";
import AdminLogin from "./components/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import AdminDashboard from "./components/admin/pages/AdminDashboard";
import PurchaseMembership from "./components/admin/pages/PurchaseMembership";
import ReplacementCard from "./components/admin/pages/ReplacementCard";
import Donation from "./components/admin/pages/Donation";
import WvVipCard from "./components/admin/pages/WvVipCard";
import PersonalizedBagTag from "./components/admin/pages/PersonalizedBagTag";
import BronzeAddon from "./components/admin/pages/BronzeAddon";
import AllTransactions from "./components/admin/pages/AllTransactions";
import AllGiftCards from "./components/admin/pages/AllGiftCards";
import AllRedemptions from "./components/admin/pages/AllRedemptions";
import AllRefunds from "./components/admin/pages/AllRefunds";
import BulkBagTags from "./components/admin/pages/BulkBagTags";
import BulkVipPackages from "./components/admin/pages/BulkVipPackages";
import BulkUpload from "./components/admin/pages/BulkUpload";
import PurchaseGiftCard from "./components/admin/pages/PurchaseGiftCard";
import PromoCodes from "./components/admin/pages/PromoCodes";
import WebPageContent from "./components/admin/pages/WebPageContent";
import AuditLog from "./components/admin/pages/AuditLog";
import AnnualDates from "./components/admin/pages/AnnualDates";

const App: React.FC = () => {
  // On the VSGA website, scroll the page back to the top on each new step (not on first load).
  const { pathname, state: routeState } = useLocation();
  const firstRoute = useRef(true);
  useEffect(() => {
    if (firstRoute.current) { firstRoute.current = false; return; }
    notifyEmbedNavigation();
  }, [pathname]);

  const navigate = useNavigate();

  const initialFormData: Golfer = {
    firstName: "",
    lastName: "",
    vip_card_type: "",
    vip_package: "",
    email: "",
    phone: "",
    id: 0,
    memberNumber: "",
    sex: "",
    date_of_birth: "",
    address: "",
    street_address_2: "",
    city: "",
    state: "VA",
    zip: "",
    donation: false,
    bagTag: false,
    personalization: "",
    donation_amount: 0,
    cart_total: 0,
    quantity: "",
    sameAsBilling: true,
    ShippingAddress: "",
    ShippingStreet_address_2: "",
    ShippingCity: "",
    ShippingState: "",
    ShippingZip: "",
    product_id: "",
    GiftVipPackage: "",
    GiftProductId: "",
    GiftPurchase: false,
    ghinNumber: "",
    bagTagPrice: 0,
    promo_value: 0,

    // add these if not already in your Golfer type
    versapaySessionId: "",
    versapayApprovalResult: null,
    versapayTransactionId: "",
    versapayOrderNumber: "",
    versapayVpOrderNumber: "",
    ccToken: "",
    ccLastFour: "",
    ccType: "",
  };

  const [formData, setFormData] = useState<Golfer>(initialFormData);

  // Record every step shown, including the first, in HubSpot (skip "/", which only redirects).
  // The package (e.g. "silver" or "silver (gift)") and email come from the shared form data; a
  // step's own updates land after this runs, so the route state passed from the previous step is
  // the fallback (it carries the email of a member found on Step 2).
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  useEffect(() => {
    if (pathname === "/") return;
    const fd = formDataRef.current;
    const fromRoute = (routeState as any)?.golfer || {};
    const pkg = fd.vip_package || fromRoute.vip_package;
    const isGift = fd.GiftPurchase || fromRoute.GiftPurchase;
    const vipPackage = pkg ? `${pkg}${isGift ? " (gift)" : ""}` : undefined;
    trackHubSpotStepView(pathname, vipPackage, fd.email || fromRoute.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one event per step, not per state change
  }, [pathname]);

  const resetFormData = () => {
    setFormData(initialFormData);
  };

  const updateField: UpdateFieldFn = (field, value) => {
    console.log("updateField called:", field, value);
    setFormData((prev) => ({
      ...prev,
      [field]: value !== undefined ? value : prev[field],
    }));
  };

  const resetStep = (step: number) => {
    setFormData((prev) => {
      const cleared = { ...prev };

      if (step === 1) {
        cleared.vip_card_type = "";
        cleared.GiftProductId = "";
        cleared.GiftPurchase = false;
        cleared.GiftVipPackage = "";
        cleared.cart_total = 0;
        cleared.sameAsBilling = true;
      }

      if (step === 2) {
        cleared.firstName = "";
        cleared.lastName = "";
        cleared.email = "";
        cleared.phone = "";
        cleared.date_of_birth = "";
        cleared.sex = "";
        cleared.memberNumber = "";
        cleared.address = "";
        cleared.city = "";
        cleared.state = "";
        cleared.zip = "";
      }

      if (step === 3) {
        cleared.id = 0;
        cleared.bagTag = false;
        cleared.donation = false;
        cleared.donation_amount = 0;
        cleared.personalization = "";
      }

      if (step === 6) {
        cleared.versapaySessionId = "";
        cleared.versapayApprovalResult = null;
        cleared.versapayTransactionId = "";
      }

      return cleared;
    });
  };

  const nextStep = () => {
    navigate("/confirm");
  };

  const prevStep = () => {
    navigate("/step5");
  };

  const resetStep6 = () => {
    resetStep(6);
  };

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/step1" replace />} />

      <Route
        path="/step1"
        element={
          <Step1
            key="step1"
            formData={formData}
            updateField={updateField}
          />
        }
      />

      <Route
        path="/step2"
        element={
          <Step2
            key="step2"
            formData={formData}
            updateField={updateField}
            resetStep={resetStep}
          />
        }
      />

      <Route
        path="/step3"
        element={
          <Step3
            key="step3"
            formData={formData}
            updateField={updateField}
            resetStep={resetStep}
          />
        }
      />

      <Route
        path="/step4"
        element={
          <Step4
            key="step4"
            formData={formData}
            updateField={updateField}
          />
        }
      />

      <Route
        path="/step5"
        element={
          <Step5
            key="step5"
            formData={formData}
            updateField={updateField}
          />
        }
      />

           <Route
        path="/step6"
        element={<Step6 formData={formData} updateField={updateField} resetStep={resetStep} />}
      />

      <Route
        path="/confirm"
        element={
          <Confirm
            key="confirm"
            formData={formData}
            updateField={updateField}
            resetFormData={resetFormData}
          />
        }
      />

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="purchase-membership" element={<PurchaseMembership />} />
        <Route path="purchase-gift-card"  element={<PurchaseGiftCard />} />
        <Route path="replacement-card" element={<ReplacementCard />} />
        <Route path="donation" element={<Donation />} />
        <Route path="wv-vip-card" element={<WvVipCard />} />
        <Route path="bag-tag" element={<PersonalizedBagTag />} />
        <Route path="bronze-addon" element={<BronzeAddon />} />
        <Route path="transactions" element={<AllTransactions />} />
        <Route path="gift-cards"   element={<AllGiftCards />} />
        <Route path="redemptions"  element={<AllRedemptions />} />
        <Route path="refunds" element={<AllRefunds />} />
        <Route path="bulk-vip-packages" element={<BulkVipPackages />} />
        <Route path="bulk-bag-tags" element={<BulkBagTags />} />
        <Route path="bulk-upload" element={<BulkUpload />} />
        <Route path="promo-codes" element={<PromoCodes />} />
        <Route path="web-page-content" element={<WebPageContent />} />
        <Route path="audit-log" element={<AuditLog />} />
        <Route path="annual-dates" element={<AnnualDates />} />
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
      </Route>
      <Route path="/admin/wizard" element={<AdminSetupWizard />} />
      <Route path="/offers/login" element={<Login />} />
      <Route path="/offers/verify" element={<OffersVerify />} />
      <Route path="/offers" element={<OffersList />} />
      <Route path="/vip/clubs" element={<GroupList />} />
      <Route path="*" element={<Navigate to="/step1" replace />} />
    </Routes>
  );
};

export default App;